import React from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";
import { DesignSystemSurface } from "../../../design-system";
import { useMediaStudio } from "./context/MediaStudioContext";
import HomeDashboard from "./sections/home/HomeDashboard";
import AdsDashboard from "./sections/ads/AdsDashboard";
import CreateHub, { CreateFlow } from "./sections/create/CreateHub";
import ReviewQueue from "./sections/review/ReviewQueue";
import CalendarQueue from "./sections/calendar/CalendarQueue";
import LibrarySection from "./sections/library/LibrarySection";
import SettingsSection, {
  SettingsTab,
} from "./sections/settings/SettingsSection";

// The studio frame: a job-based section nav synced to /media-studio/:section
// so every surface is deep-linkable. Create and Review stay routable (the
// Home quick actions and the Planner card link there) but have no nav pill:
// Home is the entry point for both.

import PipelineSection, { parsePipelineTab } from "./sections/pipeline/PipelineSection";

export type MediaStudioSection =
  | "home"
  | "ads"
  | "create"
  | "review"
  | "calendar"
  | "pipeline"
  | "library"
  | "settings";

const SECTIONS: { id: MediaStudioSection; label: string }[] = [
  { id: "home", label: "Home" },
  { id: "ads", label: "Ads" },
  { id: "calendar", label: "Calendar" },
  { id: "pipeline", label: "Delivery" },
  { id: "library", label: "Library" },
  { id: "settings", label: "Settings" },
];

const Shell = styled.div`
  min-height: 100vh;
  background: var(--color-bg);
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  position: sticky;
  top: 0;
  z-index: 5;
`;

const NavPills = styled.nav`
  display: inline-flex;
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-pill);
  padding: 3px;
  flex-wrap: wrap;
`;

const NavPill = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${(p) => (p.$active ? "var(--color-surface)" : "transparent")};
  color: ${(p) => (p.$active ? "var(--color-text)" : "var(--color-text-secondary)")};
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: ${(p) =>
    p.$active ? "var(--font-weight-semibold)" : "var(--font-weight-medium)"};
  padding: 5px var(--space-3);
  border-radius: var(--radius-pill);
  cursor: pointer;
  box-shadow: ${(p) => (p.$active ? "var(--shadow-xs)" : "none")};
  transition: background var(--duration-fast) var(--ease-standard);
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 17px;
  height: 17px;
  padding: 0 5px;
  border-radius: var(--radius-pill);
  background: var(--color-warning, #d0a020);
  color: #fff;
  font-size: 10px;
  font-weight: var(--font-weight-bold);
`;

const Main = styled.main`
  flex: 1 1 auto;
  min-width: 0;
`;

const EmptyShell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-12) var(--space-6);
  text-align: center;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
`;

// All routable sections, including the ones without a nav pill (create,
// review). parseSection must accept these or the Home quick actions and the
// Planner card would silently land back on Home.
const ALL_SECTIONS: MediaStudioSection[] = [
  "home",
  "ads",
  "create",
  "review",
  "calendar",
  "pipeline",
  "library",
  "settings",
];

const parseSection = (pathname: string): MediaStudioSection => {
  const part = pathname.split("/")[2] || "home";
  return ALL_SECTIONS.includes(part as MediaStudioSection)
    ? (part as MediaStudioSection)
    : "home";
};

const VALID_FLOWS: CreateFlow[] = ["slides", "viral", "free", "ads"];
const VALID_SETTINGS_TABS: SettingsTab[] = [
  "brand",
  "characters",
  "connections",
  "prompts",
];

export const MediaStudioShell: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { communityId, posts } = useMediaStudio();

  const section = parseSection(location.pathname);
  const query = new URLSearchParams(location.search);
  const flowParam = query.get("flow");
  const flow: CreateFlow | null = VALID_FLOWS.includes(flowParam as CreateFlow)
    ? (flowParam as CreateFlow)
    : null;
  const settingsParam = query.get("section");
  const settingsTab: SettingsTab = VALID_SETTINGS_TABS.includes(
    settingsParam as SettingsTab
  )
    ? (settingsParam as SettingsTab)
    : "brand";

  // Intra-studio navigation replaces history so the browser back button leaves
  // the studio instead of replaying every section visit.
  const navigate = (path: string) => history.replace(path);

  const goSection = (id: MediaStudioSection) =>
    navigate(id === "home" ? "/media-studio" : `/media-studio/${id}`);

  return (
    <DesignSystemSurface>
      <Shell>
        <Header>
          <NavPills>
            {SECTIONS.map((s) => (
              <NavPill
                key={s.id}
                type="button"
                $active={section === s.id}
                onClick={() => goSection(s.id)}
              >
                {s.label}
                {s.id === "review" && posts.drafts.length > 0 && (
                  <Badge>{posts.drafts.length}</Badge>
                )}
              </NavPill>
            ))}
          </NavPills>
        </Header>

        <Main>
          {!communityId ? (
            <EmptyShell>
              <div style={{ fontSize: 26 }}>🔒</div>
              <div>Sign in to a community to use the Media Studio.</div>
            </EmptyShell>
          ) : section === "home" ? (
            <HomeDashboard onNavigate={navigate} />
          ) : section === "ads" ? (
            <AdsDashboard onNavigate={navigate} />
          ) : section === "create" ? (
            <CreateHub
              flow={flow}
              onSelectFlow={(next) =>
                navigate(
                  next
                    ? `/media-studio/create?flow=${next}`
                    : "/media-studio/create"
                )
              }
            />
          ) : section === "review" ? (
            <ReviewQueue onNavigate={navigate} />
          ) : section === "calendar" ? (
            <CalendarQueue onNavigate={navigate} />
          ) : section === "pipeline" ? (
            <PipelineSection communityId={communityId} tab={parsePipelineTab(query.get("tab"))} onSelectTab={(t) => navigate(`/media-studio/pipeline?tab=${t}`)} />
          ) : section === "library" ? (
            <LibrarySection />
          ) : (
            <SettingsSection
              tab={settingsTab}
              onSelectTab={(t) => navigate(`/media-studio/settings?section=${t}`)}
            />
          )}
        </Main>
      </Shell>
    </DesignSystemSurface>
  );
};

export default MediaStudioShell;
